/* Mattoni condivisi: scheda, occhiello, pulsante, distintivo, pastiglia,
 * avvertenza. Nessuno di questi conosce un colore: conosce un TONO, e il tono
 * si traduce in una classe che legge i token. È così che la regola "verde e
 * rosso solo per il significato finanziario" resta applicabile. */

import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import type { Tono } from '../lib/metriche';

/* ---------- scheda ---------- */

export interface PropsScheda extends HTMLAttributes<HTMLDivElement> {
  variante?: 'normale' | 'incassata' | 'fitta';
  tono?: Tono | 'accento';
  children?: ReactNode;
}

export function Scheda({
  variante = 'normale',
  tono,
  className = '',
  children,
  ...resto
}: PropsScheda) {
  const classi = [
    'sb-scheda',
    variante === 'incassata' ? 'sb-scheda--incassata' : '',
    variante === 'fitta' ? 'sb-scheda--fitta' : '',
    tono && tono !== 'neutro' ? `sb-scheda--${tono}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classi} {...resto}>
      {children}
    </div>
  );
}

/* ---------- etichette ---------- */

export function Occhiello({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`sb-occhiello ${className}`}>{children}</div>;
}

export function Etichetta({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`sb-etichetta ${className}`}>{children}</div>;
}

/* ---------- pulsante ---------- */

export interface PropsPulsante extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: 'normale' | 'primario' | 'tenue' | 'cautela' | 'distruttivo' | 'nudo' | 'icona';
  largo?: boolean;
}

export function Pulsante({
  variante = 'normale',
  largo = false,
  className = '',
  type = 'button',
  children,
  ...resto
}: PropsPulsante) {
  const classi = [
    'sb-pulsante',
    variante !== 'normale' ? `sb-pulsante--${variante}` : '',
    largo ? 'sb-pulsante--largo' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={classi} {...resto}>
      {children}
    </button>
  );
}

/* ---------- distintivo ---------- */

export function Distintivo({
  tono = 'neutro',
  contorno = false,
  children,
}: {
  tono?: Tono | 'accento';
  contorno?: boolean;
  children: ReactNode;
}) {
  const classe = contorno ? 'sb-distintivo--contorno' : `sb-distintivo--${tono}`;
  return <span className={`sb-distintivo ${classe}`}>{children}</span>;
}

/**
 * Il periodo di misura in forma di distintivo. Esiste come componente proprio
 * perché la regola 2 di STATO.md — ogni numero col suo periodo — si rispetta
 * solo se metterlo è più facile che dimenticarlo.
 */
export function DistintivoPeriodo({ children }: { children: ReactNode }) {
  return <Distintivo contorno>{children}</Distintivo>;
}

/* ---------- pastiglia di filtro attivo ---------- */

export function Pastiglia({
  chiave,
  valore,
  onRimuovi,
}: {
  chiave: string;
  valore: string;
  onRimuovi: () => void;
}) {
  return (
    <button
      type="button"
      className="sb-pastiglia"
      onClick={onRimuovi}
      title={`Rimuovi il filtro ${chiave}`}
    >
      <span className="sb-pastiglia__chiave">{chiave}</span>
      <span className="sb-pastiglia__valore">{valore}</span>
      <span className="sb-pastiglia__chiudi" aria-hidden="true">
        <IconaChiudi />
      </span>
    </button>
  );
}

/* ---------- avvertenza in linea ---------- */

export function Avvertenza({
  livello = 'nota',
  children,
}: {
  livello?: 'nota' | 'cautela' | 'grave';
  children: ReactNode;
}) {
  return (
    <div className={`sb-avvertenza sb-avvertenza--${livello}`} role={livello === 'grave' ? 'alert' : undefined}>
      <span className="sb-avvertenza__icona" aria-hidden="true">
        {livello === 'nota' ? <IconaInfo /> : <IconaAttenzione />}
      </span>
      <span>{children}</span>
    </div>
  );
}

/* ---------- separatori ---------- */

export function Divisore() {
  return <hr className="sb-divisore" />;
}

export function DivisoreVerticale() {
  return <span className="sb-divisore-verticale" aria-hidden="true" />;
}

/* ---------- icone
 * In linea e non da una libreria: sono sei tratti, e una dipendenza esterna
 * per sei tratti è una dipendenza che si aggiorna e rompe. ---------- */

export function IconaChiudi({ dim = 15 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

export function IconaInfo({ dim = 16 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="none" stroke="var(--sb-accento-chiaro)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

export function IconaAttenzione({ dim = 16 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

export function IconaCerca({ dim = 15 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </svg>
  );
}

export function IconaFiltri({ dim = 15 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 6h18M7 12h10M10 18h4" />
    </svg>
  );
}

export function IconaDatabase({ dim = 15 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3z M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6 M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </svg>
  );
}

export function IconaFreccia({ dim = 14, verso = 'giu' }: { dim?: number; verso?: 'giu' | 'su' | 'sinistra' }) {
  const rotazione = verso === 'su' ? 'rotate(180deg)' : verso === 'sinistra' ? 'rotate(90deg)' : 'none';
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: rotazione, transition: `transform var(--sb-tempo-medio)` }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function IconaAggiorna({ dim = 13 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8 M21 3v5h-5" />
    </svg>
  );
}

export function IconaAvvia({ dim = 14 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4l14 8-14 8z" />
    </svg>
  );
}

export function IconaFerma({ dim = 17 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 6h12v12H6z" />
    </svg>
  );
}

export function IconaSpunta({ dim = 12 }: { dim?: number }) {
  return (
    <svg width={dim} height={dim} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}
